import { denormalizeTypeId, normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import { useCallback, useMemo } from 'react';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { AssociatedObjectsCardContext } from '../../context/AssociatedObjectsCardContext';
import { CRM_FEATURES } from 'crm-cards-sdk/types/CrmFeatures';
import { useCardRenderMarkers } from 'crm-cards-sdk/hooks/useCardRenderMarkers';
import CardContentLoadingComponent from 'crm-cards-sdk/components/skeletons/CardContentLoadingComponent';
import { CardEmptyState } from '../../extensions/constants/BehaviorTypes';
import { useBehavior } from '../../extensions/hooks/useBehavior';
import { validateObjectTypeId } from '../../types/ObjectTypeId';
import { useAssociatedObjectsCardDataContext } from '../../context/AssociatedObjectsCardDataContext';
import AssociatedObjectsCardContent from '../AssociatedObjectsCardContent';
import { CrmLogger } from 'customer-data-tracking/loggers';
import isPortalSpecificObjectType from 'customer-data-objects/crmObject/isPortalSpecificObjectType';
import CardBottomContent from '../CardBottomContent';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function AssociatedObjectsCardContentSdkDataProvider({
  cardId,
  hasFeature,
  hasScope,
  isUngated,
  objectId,
  objectTypeId,
  propertyNames,
  sortConfiguration,
  readOnly,
  toObjectTypeId,
  propertySource
}) {
  const {
    SuccessMarker
  } = useCardRenderMarkers();
  const propertySourceInfo = useMemo(() => {
    return {
      propertySource
    };
  }, [propertySource]);
  const sendPreviewObjectMessage = useSendCrmMessageTopic(TOPIC_NAMES.PREVIEW_OBJECT);
  const setPreviewConfig = useCallback(config => {
    if (config) {
      const normalizedToObjectTypeId = normalizeTypeId(config.objectTypeId);
      const normalizedFromObjectTypeId = normalizeTypeId(objectTypeId);
      CrmLogger.log('recordInteractions', {
        toObjectTypeId: isPortalSpecificObjectType(normalizedToObjectTypeId) ? 'CUSTOM_OBJECT' : normalizedToObjectTypeId,
        fromObjectTypeId: isPortalSpecificObjectType(normalizedFromObjectTypeId) ? 'CUSTOM_OBJECT' : normalizedFromObjectTypeId,
        action: 'Clicked preview button'
      });
      sendPreviewObjectMessage({
        objectTypeId: normalizeTypeId(config.objectTypeId),
        objectId: `${config.objectId}`
      });
    }
  }, [objectTypeId, sendPreviewObjectMessage]);
  const sendUpdateObject = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECT);
  const sendObjectCreated = useSendCrmMessageTopic(TOPIC_NAMES.OBJECT_CREATED);
  const sendUpdateAssociation = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_ASSOCIATIONS);
  const sendOpenActivityCreator = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_ACTIVITY_CREATOR);
  const sendOpenCallWidget = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_CALL_WIDGET);
  const handleEmailClick = useCallback((recipientObjectTypeId, recipientId) => {
    sendOpenActivityCreator({
      interactionType: 'email',
      objectId: Number(recipientId),
      objectTypeId: recipientObjectTypeId
    });
  }, [sendOpenActivityCreator]);
  const handlePhoneClick = useCallback((recipientObjectTypeId, recipientId) => {
    sendOpenCallWidget({
      interactionType: 'call',
      objectId: Number(recipientId),
      objectTypeId: recipientObjectTypeId
    });
  }, [sendOpenCallWidget]);
  const handleObjectUpdate = useCallback(({
    properties
  }) => {
    sendUpdateObject({
      propertyNames: properties.map((_val, key) => key).toArray(),
      objectTypeId,
      objectId: String(objectId)
    });
  }, [objectId, objectTypeId, sendUpdateObject]);
  const handleAssociationUpdate = useCallback(({
    objectId: updatedObjectId,
    objectType: updatedObjectType,
    associationObjectType
  }) => {
    sendUpdateAssociation({
      objectId: String(updatedObjectId),
      objectTypeId: normalizeTypeId(updatedObjectType),
      toObjectTypeId: normalizeTypeId(associationObjectType)
    });
  }, [sendUpdateAssociation]);
  const handleObjectCreate = useCallback(({
    associationType,
    objectId: createdObjectId,
    addAnother
  }) => {
    sendObjectCreated({
      objectId: String(createdObjectId),
      objectTypeId: normalizeTypeId(associationType),
      preventRedirect: !!addAnother
    });
  }, [sendObjectCreated]);
  const {
    allAssociationTypesBetweenObjectIdsData,
    associationAuditSourcesData,
    associationsData,
    associationTypesData,
    isLoading,
    objectTypePermissions,
    onViewMore,
    primaryDisplayLabel,
    propertyDefinitions,
    refetchAllAssociationTypesBetweenObjectIdsData,
    refetchAssociationsData,
    userPermissions
  } = useAssociatedObjectsCardDataContext();
  const CardEmptyStateComponent = useBehavior(CardEmptyState, validateObjectTypeId(toObjectTypeId), hasScope, isUngated);
  const contextValue = useMemo(() => ({
    associationAuditSourcesData: associationAuditSourcesData && associationAuditSourcesData,
    associationsData: {
      impliedUnlabeledAssociationObjects: associationsData === null || associationsData === void 0 ? void 0 : associationsData.impliedUnlabeledAssociationObjects,
      primaryAssociationObject: associationsData === null || associationsData === void 0 ? void 0 : associationsData.primaryAssociationObject,
      associationsDataError: associationsData === null || associationsData === void 0 ? void 0 : associationsData.associationsDataError
    },
    associationTypesData: associationTypesData,
    allAssociationTypesBetweenObjectIdsData,
    cardId,
    hasScope,
    isEmbedded: false,
    isUngated,
    objectId: Number(objectId),
    objectTypeId,
    objectTypePermissions: objectTypePermissions,
    onAssociationUpdate: handleAssociationUpdate,
    onObjectCreate: handleObjectCreate,
    onObjectUpdate: handleObjectUpdate,
    onPreview: setPreviewConfig,
    onEmailClick: hasFeature(CRM_FEATURES.COMMUNICATOR_EMAIL) ? handleEmailClick : undefined,
    onPhoneClick: hasFeature(CRM_FEATURES.COMMUNICATOR_CALLING) ? handlePhoneClick : undefined,
    primaryDisplayLabel: primaryDisplayLabel,
    propertyDefinitions,
    propertyNames,
    propertySource: propertySourceInfo,
    sortConfiguration,
    readOnly,
    refetchAssociationsData,
    refetchAllAssociationTypesBetweenObjectIdsData,
    toObjectTypeId,
    userPermissions: userPermissions
  }), [allAssociationTypesBetweenObjectIdsData, associationAuditSourcesData, associationTypesData, associationsData === null || associationsData === void 0 ? void 0 : associationsData.associationsDataError, associationsData === null || associationsData === void 0 ? void 0 : associationsData.impliedUnlabeledAssociationObjects, associationsData === null || associationsData === void 0 ? void 0 : associationsData.primaryAssociationObject, cardId, handleAssociationUpdate, handleEmailClick, handleObjectCreate, handleObjectUpdate, handlePhoneClick, hasFeature, hasScope, isUngated, objectId, objectTypeId, objectTypePermissions, primaryDisplayLabel, propertyDefinitions, propertyNames, propertySourceInfo, readOnly, refetchAllAssociationTypesBetweenObjectIdsData, refetchAssociationsData, setPreviewConfig, sortConfiguration, toObjectTypeId, userPermissions]);
  let content;
  if (isLoading && !(associationsData !== null && associationsData !== void 0 && associationsData.impliedUnlabeledAssociationObjects)) {
    content = /*#__PURE__*/_jsx(CardContentLoadingComponent, {});
  } else if (!(associationsData !== null && associationsData !== void 0 && associationsData.impliedUnlabeledAssociationObjects) || !associationsData.impliedUnlabeledAssociationObjects.results.length) {
    content = /*#__PURE__*/_jsxs(_Fragment, {
      children: [SuccessMarker && /*#__PURE__*/_jsx(SuccessMarker, {}), /*#__PURE__*/_jsx(CardEmptyStateComponent, {
        cardId: cardId,
        numberOfProperties: propertyNames.length,
        fromObjectTypeId: toObjectTypeId,
        objectTypeId: objectTypeId,
        objectId: objectId,
        hasScope: hasScope,
        isUngated: isUngated,
        toObjectPluralForm: (associationTypesData === null || associationTypesData === void 0 ? void 0 : associationTypesData.associationTypes[0]) && associationTypesData.associationTypes[0].toObjectTypeDefinition && associationTypesData.associationTypes[0].toObjectTypeDefinition.pluralForm || ''
      })]
    });
  } else {
    content = /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(AssociatedObjectsCardContent, {
        cardId: cardId,
        objectId: objectId,
        objectType: denormalizeTypeId(objectTypeId),
        onPreview: setPreviewConfig,
        onViewMore: onViewMore,
        sortConfiguration: sortConfiguration,
        toObjectTypeId: toObjectTypeId
      }), SuccessMarker && /*#__PURE__*/_jsx(SuccessMarker, {})]
    });
  }
  return /*#__PURE__*/_jsx("div", {
    "data-test-id": `associated-objects-card-content-${toObjectTypeId}`,
    children: /*#__PURE__*/_jsxs(AssociatedObjectsCardContext.Provider, {
      value: contextValue,
      children: [content, /*#__PURE__*/_jsx(CardBottomContent, {
        toObjectTypeId: toObjectTypeId,
        fromObjectTypeId: denormalizeTypeId(objectTypeId),
        fromObjectId: objectId,
        primaryDisplayLabel: primaryDisplayLabel || null
      })]
    })
  });
}
export default AssociatedObjectsCardContentSdkDataProvider;